import PropTypes from "prop-types"
import React, { forwardRef } from "react"

import { ErrorMessage } from "../ErrorMessage"
import { Label } from "../Label"
import { Tooltip } from "../Tooltip"
import { AuxText, InputContainer as Container, InputDescription as Description, InnerContainer } from "./styled"
import { filterAvailableProps } from "./utils"

/**
 * An input that can have label, descriptions, errors and popover.
 * Types can be passed so it can be used for hidden, password,email, date, etc.
 *
 * Iteraction props are filtered, use handleChange to receive values.
 */
export const Input = forwardRef(
  (
    {
      label,
      error,
      required,
      id,
      description,
      value,
      handleChange,
      name,
      help,
      onChange,
      helpAnchor = "?",
      type,
      allowNegatives = false,
      className,
      auxiliarText,
      onlyInput,
      ...otherProps
    },
    ref,
  ) => {
    if (type === "checkbox" || type === "radio") {
      console.warn(`Please use the specific component for ${type}`)

      return null
    }

    const props = filterAvailableProps(otherProps)
    const hasError = Boolean(error)
    const isControlled = Boolean(handleChange) || Boolean(onChange)
    const htmlFor = id
    const inputProps = {
      ...props,
      required,
      id,
      type,
      name,
    }

    const handleOnChange = (e) => {
      if (!isControlled) {
        return
      }

      e.preventDefault()

      if (typeof handleChange === "function") handleChange(e.target.value, name, e)
      else if (typeof onChange === "function") onChange(e)
    }

    if (isControlled) {
      inputProps.onChange = handleOnChange
      inputProps.value = value
    } else {
      inputProps.defaultValue = props.defaultValue || value
    }

    let labelContent = label

    if (Boolean(help)) {
      labelContent = (
        <>
          {label}
          <Tooltip autoFit content={help}>
            {helpAnchor}
          </Tooltip>
        </>
      )
    }

    const handleKeyDown = (event) => {
      if (props.onKeyDown) {
        return props.onKeyDown(event)
      }

      if (type !== "number") return true
      // backspace or delete
      const isDeletable = event.keyCode === 8 || event.keyCode === 46
      let isAllowed = isDeletable || !isNaN(Number(event.key))

      if (allowNegatives) {
        // for unit tests, we need to check the key rather than keyCode
        isAllowed = isAllowed || event.key === "-"
      }

      if (!isAllowed) {
        event.preventDefault()
      }

      return isAllowed
    }

    const inputContent = (
      <>
        <input {...inputProps} onKeyDown={handleKeyDown} ref={ref} />
        {auxiliarText && <AuxText>{auxiliarText}</AuxText>}
      </>
    )

    if (onlyInput) {
      return inputContent
    }

    return (
      <Container
        data-testid="input"
        $hasError={hasError}
        data-has-error={String(Boolean(hasError))}
        className={className}
      >
        {labelContent && (
          <Label required={required} htmlFor={htmlFor}>
            {labelContent}
          </Label>
        )}
        <InnerContainer>{inputContent}</InnerContainer>
        {hasError && <ErrorMessage>{error}</ErrorMessage>}
        {description && <Description>{description}</Description>}
      </Container>
    )
  },
)

Input.displayName = "tsr-Input"

Input.propTypes = {
  /**
   * The label for the component
   */
  label: PropTypes.node,
  /**
   * If present the content will be shown as ErrorMessage
   */
  error: PropTypes.node,
  /**
   * Additional information about the input that will be always visible
   */
  description: PropTypes.node,
  /**
   * The text to serve as the help anchor
   * @default "?"
   */
  helpAnchor: PropTypes.node,
  /**
   * If required label will appear with a hint
   */
  required: PropTypes.bool,
  /**
   * A callback that receives (value, name, syntheticEvent)
   */
  handleChange: PropTypes.func,
  /**
   * The contentes of the help text that will appear on acnhor hover
   */
  help: PropTypes.node,
  /**
   * Defaults to text
   */
  type: PropTypes.string,
  /**
   * A text that will appear on the side of the input
   */
  auxiliarText: PropTypes.string,
  /**
   * If true the default key handler will allow negative numbers
   */
  allowNegatives: PropTypes.bool,
  /**
   * If true only the input content will be rendered, no labels, errors and so on
   */
  onlyInput: PropTypes.bool,
}
